import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Queue, useGraphneAPI } from '@nexelem/graphne-api';
import { useMemo } from 'react';
import { productionQueuesQueryKey } from '../../../../../queryHooks/useProductionsQueues';
import { QueueAreaWithExternalId } from '../../types';

export const useGetAllQueuesAndAreas = () => {
  const api = useGraphneAPI();

  const { data: queues } = useQuery({
    queryKey: productionQueuesQueryKey,
    queryFn: () => api.getGraphneQueues(),
    refetchOnMount: false,
  });

  const { queueOptions, queueAreaOptions } = useMemo(() => {
    if (!queues) {
      return { queueOptions: [], queueAreaOptions: [] };
    }

    const uniqueAreas = new Map();
    queues.forEach((queue: Queue) => {
      if (queue.area && queue.area.id && queue.area.name) {
        uniqueAreas.set(queue.area.id, {
          key: queue.area.name,
          value: queue.area.id,
          color: queue.area.hex,
        });
      }
    });
    const queueAreaOptions = Array.from(uniqueAreas.values());

    const uniqueWorkstations = new Map();
    queues.forEach((queue: Queue) => {
      if (queue.name && queue.id) {
        uniqueWorkstations.set(queue.shortName, {
          key: queue.shortName,
          value: queue.id,
        });
      }
    });
    const queueOptions = Array.from(uniqueWorkstations.values());

    return { queueOptions, queueAreaOptions };
  }, [queues]);

  return {
    queueOptions,
    queueAreaOptions,
  };
};

export const useGetAllQueuesAndAreasSuspense = () => {
  const api = useGraphneAPI();

  const { data } = useSuspenseQuery({
    queryKey: productionQueuesQueryKey,
    queryFn: () => api.getGraphneQueues(),
    refetchOnMount: false,
  });

  const areas = data.reduce((acc, queue) => {
    if (queue.area.id && !acc.find((area) => area.id === queue.area.id)) {
      return [...acc, { ...queue.area, externalId: queue.externalId as { id: number } }];
    }
    return acc;
  }, [] as QueueAreaWithExternalId[]);

  return {
    queues: data ?? [],
    areas,
  };
};
